/**
 * Pausable SLA maths (decision D10).
 *
 * Lives in shared, not on the server, because the client applies the same
 * calculation optimistically while offline (CLAUDE.md Rule 4). Both sides must
 * arrive at the same deadline or the queued write will disagree on sync.
 */
import { z } from 'zod';
const MINUTE_MS = 60_000;
/** Body of POST /api/tickets/:id/hold. */
export const holdTicketBodySchema = z.object({
    reason: z.string().trim().min(1, 'A reason is required').max(500),
});
/**
 * Whole minutes between two instants, truncated — matching `differenceInMinutes`.
 *
 * Truncation means a hold under 60s extends nothing, and a 90s hold extends by one
 * minute rather than 1.5. The residue is always in the property's favour and never
 * the team's; see docs/SPEC.md §9.2 if that needs revisiting.
 */
export function differenceInWholeMinutes(later, earlier) {
    return Math.floor((later.getTime() - earlier.getTime()) / MINUTE_MS);
}
export class NotOnHoldError extends Error {
    constructor() {
        super('Ticket is not on hold: currentHoldStartedAt is null');
        this.name = 'NotOnHoldError';
    }
}
/**
 * Closes an open hold. Pure: callers supply `now` so this stays testable and gives
 * the same answer on a phone with a skewed clock as on the server.
 */
export function computeResume(state, now) {
    if (!state.currentHoldStartedAt)
        throw new NotOnHoldError();
    // A clock that jumped backwards must never shorten the deadline.
    const holdMins = Math.max(0, differenceInWholeMinutes(now, state.currentHoldStartedAt));
    return {
        holdMins,
        totalHoldDurationMins: state.totalHoldDurationMins + holdMins,
        slaResolveDeadline: new Date(state.slaResolveDeadline.getTime() + holdMins * MINUTE_MS),
    };
}
/**
 * Elapsed minutes of a hold that is still open, for live display. Returns 0 when the
 * ticket is not on hold.
 */
export function elapsedHoldMinutes(state, now) {
    if (!state.currentHoldStartedAt)
        return 0;
    return Math.max(0, differenceInWholeMinutes(now, state.currentHoldStartedAt));
}
/**
 * The resolve deadline as it stands right now, crediting an open hold (D6, D10).
 *
 * Closed holds are already banked into `slaResolveDeadline` — `computeResume` extends
 * it when work restarts. The hold still running is not banked yet, so any consumer
 * comparing "is this late" against the raw column bills the team for a vendor's wait
 * that is still being waited out. One rule, two consumers: the CSV export's
 * `breachedResolve` and the queue's breach badges must never disagree about whether
 * a held job is overdue.
 *
 * A useful consequence: lateness accrued *before* the hold began stays accrued. The
 * credit grows one-for-one with the clock, so a job that was 20 minutes late when the
 * vendor was called is exactly 20 minutes late until work resumes — frozen, not
 * forgiven, and not growing.
 */
export function effectiveResolveDeadline(state, now) {
    return new Date(state.slaResolveDeadline.getTime() + elapsedHoldMinutes(state, now) * MINUTE_MS);
}
//# sourceMappingURL=hold.js.map